import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LockOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";
import io from "socket.io-client";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";

dayjs.locale("es");

const { Title, Text } = Typography;

const SLOT_STEP_MINUTES = 15;
const WEEKDAY_START = 10;
const WEEKDAY_END = 20;
const SATURDAY_START = 9;
const SATURDAY_END = 18;
const WEEKDAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
];

const pageCardStyle = {
  borderRadius: 24,
  border: "1px solid #edf2f7",
  boxShadow: "0 20px 55px rgba(15, 23, 42, 0.06)",
};

const summaryCardStyle = {
  ...pageCardStyle,
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.05)",
};

const selectionPanelStyle = {
  borderRadius: 20,
  padding: 18,
  border: "1px solid rgba(255,255,255,0.45)",
  background: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(10px)",
};

const legendItemStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderRadius: 999,
  border: "1px solid #e5e7eb",
  background: "#fff",
};

const getBusinessHours = (dateValue) => {
  const selected = dayjs(dateValue);

  if (!selected.isValid()) return null;

  const weekday = selected.day();
  if (weekday === 0) return null;

  return {
    startHour: weekday === 6 ? SATURDAY_START : WEEKDAY_START,
    endHour: weekday === 6 ? SATURDAY_END : WEEKDAY_END,
  };
};

const formatTime = (hour, minute) =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

const capitalizeLabel = (value = "") =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const getBusinessHoursLabel = (dateValue) => {
  const businessHours = getBusinessHours(dateValue);

  if (!businessHours) {
    return "Cerrado los domingos";
  }

  return `${formatTime(businessHours.startHour, 0)} - ${formatTime(
    businessHours.endHour,
    0
  )}`;
};

const getSlotButtonStyle = ({ isBlocked, isReserved }) => {
  if (isReserved) {
    return {
      height: 52,
      borderRadius: 16,
      borderColor: "#e5e7eb",
      background: "#f3f4f6",
      color: "#9ca3af",
      boxShadow: "none",
      fontWeight: 600,
    };
  }

  if (isBlocked) {
    return {
      height: 52,
      borderRadius: 16,
      borderColor: "#111827",
      background: "#111827",
      color: "#ffffff",
      boxShadow: "0 12px 24px rgba(17, 24, 39, 0.18)",
      fontWeight: 700,
    };
  }

  return {
    height: 52,
    borderRadius: 16,
    borderColor: "#bfdbfe",
    background: "#ffffff",
    color: "#2563eb",
    boxShadow: "0 10px 24px rgba(37, 99, 235, 0.08)",
    fontWeight: 700,
  };
};

export default function CreateTimeSlot() {
  const [loadingTime, setLoadingTime] = useState("");
  const [loadingFullDay, setLoadingFullDay] = useState(false);
  const [loadingSalonFullDay, setLoadingSalonFullDay] = useState(false);
  const [loadingWeeklySchedule, setLoadingWeeklySchedule] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [availabilityOverrides, setAvailabilityOverrides] = useState([]);
  const [salonClosures, setSalonClosures] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [weeklyConfigured, setWeeklyConfigured] = useState(false);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_BASE}/employees`);
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      message.error("Error al cargar empleados");
    }
  };

  const fetchBlocks = async () => {
    try {
      setFetching(true);
      const res = await axios.get(`${API_BASE}/timeSlot`);
      const data = Array.isArray(res.data) ? res.data : [];
      setBlocks(data.filter((item) => item.isAvailable === false));
      setAvailabilityOverrides(data.filter((item) => item.isAvailable === true));
    } catch (err) {
      console.error(err);
      message.error("Error al cargar bloqueos");
    } finally {
      setFetching(false);
    }
  };

  const fetchSalonClosures = async () => {
    try {
      const res = await axios.get(`${API_BASE}/timeSlot/salon-closures`, {
        headers: getAuthHeaders(),
      });
      setSalonClosures(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      message.error("Error al cargar cierres del salón");
    }
  };

  const fetchReservations = async () => {
    try {
      const res = await axios.get(`${API_BASE}/booking`, {
        headers: getAuthHeaders(),
      });
      setReservations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      message.error("Error al cargar reservas");
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchBlocks();
    fetchSalonClosures();
    fetchReservations();
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleAvailabilityUpdate = () => {
      fetchBlocks();
      fetchSalonClosures();
      fetchReservations();
    };

    socket.on("availability:updated", handleAvailabilityUpdate);

    return () => {
      socket.off("availability:updated", handleAvailabilityUpdate);
      socket.disconnect();
    };
  }, []);

  const selectedDateKey =
    selectedDate && dayjs(selectedDate).isValid()
      ? dayjs(selectedDate).format("YYYY-MM-DD")
      : null;
  const businessHours = getBusinessHours(selectedDate);

  const selectedEmployee = useMemo(
    () =>
      employees.find(
        (employee) => Number(employee.id) === Number(selectedEmployeeId)
      ) || null,
    [employees, selectedEmployeeId]
  );

  useEffect(() => {
    if (!selectedEmployeeId) {
      setWeeklySchedule({});
      setWeeklyConfigured(false);
      return;
    }

    let active = true;
    const fetchWeeklySchedule = async () => {
      setLoadingWeeklySchedule(true);
      try {
        const response = await axios.get(
          `${API_BASE}/timeSlot/weekly/${selectedEmployeeId}`,
          { headers: getAuthHeaders() }
        );
        if (!active) return;
        setWeeklySchedule(response.data?.schedule || {});
        setWeeklyConfigured(Boolean(response.data?.configured));
      } catch (err) {
        if (active) message.error("No se pudo cargar la disponibilidad semanal");
      } finally {
        if (active) setLoadingWeeklySchedule(false);
      }
    };

    fetchWeeklySchedule();
    return () => {
      active = false;
    };
  }, [selectedEmployeeId]);

  const visibleBlocks = useMemo(() => {
    return blocks.filter(
      (item) =>
        Number(item.employeeId) === Number(selectedEmployeeId) &&
        item.date === selectedDateKey
    );
  }, [blocks, selectedEmployeeId, selectedDateKey]);

  const blockedByTime = useMemo(() => {
    return new Map(
      visibleBlocks.map((item) => [String(item.startTime).slice(0, 5), item])
    );
  }, [visibleBlocks]);

  const allowedOverridesByTime = useMemo(() => {
    return new Map(
      availabilityOverrides
        .filter(
          (item) =>
            Number(item.employeeId) === Number(selectedEmployeeId) &&
            item.date === selectedDateKey
        )
        .map((item) => [String(item.startTime).slice(0, 5), item])
    );
  }, [availabilityOverrides, selectedEmployeeId, selectedDateKey]);

  const weeklyAllowedTimes = useMemo(() => {
    if (!weeklyConfigured || !selectedDateKey) return null;
    return new Set(weeklySchedule[String(dayjs(selectedDateKey).day())] || []);
  }, [weeklyConfigured, weeklySchedule, selectedDateKey]);

  const isPermanentlyBlockedTime = (time) =>
    weeklyAllowedTimes !== null &&
    !weeklyAllowedTimes.has(time) &&
    !allowedOverridesByTime.has(time);

  const reservedTimes = useMemo(() => {
    return new Set(
      reservations
        .filter(
          (item) =>
            Number(item.employeeId) === Number(selectedEmployeeId) &&
            item.date === selectedDateKey &&
            ["reservado", "en_curso", "pendiente", "confirmado"].includes(
              item.status
            )
        )
        .flatMap((item) => {
          const start = dayjs(`2000-01-01 ${item.time}`, "YYYY-MM-DD HH:mm");
          const duration = Number(item.duration || 60);
          const times = [];

          for (let offset = 0; offset < duration; offset += SLOT_STEP_MINUTES) {
            times.push(start.add(offset, "minute").format("HH:mm"));
          }

          return times;
        })
    );
  }, [reservations, selectedEmployeeId, selectedDateKey]);

  const slots = useMemo(() => {
    if (!businessHours) return [];

    const result = [];
    const { startHour, endHour } = businessHours;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += SLOT_STEP_MINUTES) {
        result.push(formatTime(hour, minute));
      }
    }

    return result;
  }, [businessHours]);

  const isPermanentFullDayBlocked = useMemo(
    () =>
      Boolean(
        slots.length &&
        weeklyAllowedTimes !== null &&
        slots.every(
          (time) => !weeklyAllowedTimes.has(time) && !allowedOverridesByTime.has(time)
        )
      ),
    [slots, weeklyAllowedTimes, allowedOverridesByTime]
  );

  const manageableSlots = useMemo(
    () => slots.filter((time) => !reservedTimes.has(time)),
    [slots, reservedTimes]
  );

  const isFullDayBlocked = useMemo(() => {
    if (!manageableSlots.length) return false;
    return manageableSlots.every((time) => blockedByTime.has(time));
  }, [manageableSlots, blockedByTime]);

  const isSalonFullDayBlocked = useMemo(() => {
    return salonClosures.some((closure) => closure.date === selectedDateKey);
  }, [salonClosures, selectedDateKey]);

  const blockedCount = useMemo(
    () =>
      manageableSlots.filter(
        (time) =>
          blockedByTime.has(time) ||
          (weeklyAllowedTimes !== null &&
            !weeklyAllowedTimes.has(time) &&
            !allowedOverridesByTime.has(time))
      ).length,
    [manageableSlots, blockedByTime, weeklyAllowedTimes, allowedOverridesByTime]
  );

  const availableCount = useMemo(
    () =>
      slots.filter(
        (time) =>
          !reservedTimes.has(time) &&
          !blockedByTime.has(time) &&
          !(
            weeklyAllowedTimes !== null &&
            !weeklyAllowedTimes.has(time) &&
            !allowedOverridesByTime.has(time)
          )
      ).length,
    [slots, reservedTimes, blockedByTime, weeklyAllowedTimes, allowedOverridesByTime]
  );

  const reservedCount = reservedTimes.size;

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate || !dayjs(selectedDate).isValid()) {
      return "Sin fecha seleccionada";
    }

    return capitalizeLabel(dayjs(selectedDate).format("dddd D [de] MMMM [de] YYYY"));
  }, [selectedDate]);

  const businessHoursLabel = useMemo(
    () => getBusinessHoursLabel(selectedDate),
    [selectedDate]
  );

  const salonStatus = useMemo(() => {
    if (!selectedDateKey) {
      return { text: "Sin fecha", color: "default" };
    }

    return isSalonFullDayBlocked
      ? { text: "Salón cerrado", color: "red" }
      : { text: "Salón disponible", color: "green" };
  }, [isSalonFullDayBlocked, selectedDateKey]);

  const employeeStatus = useMemo(() => {
    if (!selectedEmployeeId) {
      return { text: "Elegí un profesional", color: "default" };
    }

    if (!businessHours) {
      return { text: "Sin agenda en domingo", color: "gold" };
    }

    if (isSalonFullDayBlocked) {
      return { text: "Depende del cierre general", color: "red" };
    }

    if (isPermanentFullDayBlocked) {
      return { text: "Agenda bloqueada permanentemente", color: "red" };
    }

    if (weeklyConfigured) {
      return { text: "Bloqueo permanente con excepciones", color: "orange" };
    }

    if (isFullDayBlocked) {
      return { text: "Fecha bloqueada completa", color: "red" };
    }

    if (!manageableSlots.length) {
      return { text: "Sin horarios para gestionar", color: "gold" };
    }

    return { text: "Agenda disponible", color: "green" };
  }, [
    selectedEmployeeId,
    businessHours,
    isSalonFullDayBlocked,
    isPermanentFullDayBlocked,
    weeklyConfigured,
    isFullDayBlocked,
    manageableSlots.length,
  ]);

  const handleToggleBlock = async (time) => {
    if (!selectedEmployeeId || !selectedDateKey) {
      message.warning("Seleccioná empleado y fecha primero");
      return;
    }

    if (isSalonFullDayBlocked) {
      message.warning("La fecha está bloqueada para todo el salón");
      return;
    }

    const currentBlock = blockedByTime.get(time);
    const currentAllowedOverride = allowedOverridesByTime.get(time);
    const restrictedByPermanentSchedule =
      weeklyAllowedTimes !== null && !weeklyAllowedTimes.has(time);
    setLoadingTime(time);

    try {
      if (restrictedByPermanentSchedule) {
        if (currentAllowedOverride) {
          await axios.delete(`${API_BASE}/timeSlot/${currentAllowedOverride.id}`, {
            headers: getAuthHeaders(),
          });
          message.success("El horario volvió a quedar bloqueado");
        } else {
          if (currentBlock) {
            await axios.delete(`${API_BASE}/timeSlot/${currentBlock.id}`, {
              headers: getAuthHeaders(),
            });
          }
          const endTime = dayjs(`2000-01-01 ${time}`, "YYYY-MM-DD HH:mm")
            .add(SLOT_STEP_MINUTES, "minute")
            .format("HH:mm");
          await axios.post(
            `${API_BASE}/timeSlot`,
            {
              date: selectedDateKey,
              startTime: time,
              endTime,
              employeeId: selectedEmployeeId,
              isAvailable: true,
            },
            { headers: getAuthHeaders() }
          );
          message.success(`Horario ${time} habilitado solo para esta fecha`);
        }
      } else if (currentBlock) {
        await axios.delete(`${API_BASE}/timeSlot/${currentBlock.id}`, {
          headers: getAuthHeaders(),
        });
        message.success("Bloqueo eliminado");
      } else {
        const endTime = dayjs(`2000-01-01 ${time}`, "YYYY-MM-DD HH:mm")
          .add(SLOT_STEP_MINUTES, "minute")
          .format("HH:mm");

        await axios.post(
          `${API_BASE}/timeSlot`,
          {
            date: selectedDateKey,
            startTime: time,
            endTime,
            employeeId: selectedEmployeeId,
          },
          {
            headers: getAuthHeaders(),
          }
        );
        message.success("Horario bloqueado");
      }

      fetchBlocks();
      fetchReservations();
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.message || "No se pudo actualizar el bloqueo"
      );
    } finally {
      setLoadingTime("");
    }
  };

  const handleToggleFullDay = async () => {
    if (!selectedEmployeeId || !selectedDateKey) {
      message.warning("Seleccioná empleado y fecha primero");
      return;
    }

    if (!businessHours) {
      message.warning("No se pueden administrar bloqueos para domingos");
      return;
    }

    if (!manageableSlots.length) {
      message.warning("No hay horarios disponibles para bloquear en esta fecha");
      return;
    }

    setLoadingFullDay(true);

    try {
      if (isFullDayBlocked) {
        await Promise.all(
          manageableSlots
            .map((time) => blockedByTime.get(time))
            .filter(Boolean)
            .map((block) =>
              axios.delete(`${API_BASE}/timeSlot/${block.id}`, {
                headers: getAuthHeaders(),
              })
            )
        );
        message.success("Se desbloqueó el día completo");
      } else {
        await Promise.all(
          manageableSlots
            .filter((time) => !blockedByTime.has(time))
            .map((time) => {
              const endTime = dayjs(`2000-01-01 ${time}`, "YYYY-MM-DD HH:mm")
                .add(SLOT_STEP_MINUTES, "minute")
                .format("HH:mm");

              return axios.post(
                `${API_BASE}/timeSlot`,
                {
                  date: selectedDateKey,
                  startTime: time,
                  endTime,
                  employeeId: selectedEmployeeId,
                },
                {
                  headers: getAuthHeaders(),
                }
              );
            })
        );
        message.success("Se bloqueó el día completo");
      }

      fetchBlocks();
      fetchReservations();
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.message ||
          "No se pudo actualizar el bloqueo del día"
      );
    } finally {
      setLoadingFullDay(false);
    }
  };

  const handleBlockAllWeekly = async () => {
    if (!selectedEmployeeId) return;

    const blockedSchedule = Object.fromEntries(
      WEEKDAYS.map((day) => [String(day.value), []])
    );
    setLoadingWeeklySchedule(true);
    try {
      const response = await axios.put(
        `${API_BASE}/timeSlot/weekly/${selectedEmployeeId}`,
        { schedule: blockedSchedule },
        { headers: getAuthHeaders() }
      );
      setWeeklySchedule(response.data?.schedule || blockedSchedule);
      setWeeklyConfigured(true);
      message.success(
        `Se bloquearon todas las fechas y horarios de ${selectedEmployee?.name || "este profesional"}`
      );
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || "No se pudo bloquear toda la agenda");
    } finally {
      setLoadingWeeklySchedule(false);
    }
  };

  const handleRemoveWeeklySchedule = async () => {
    if (!selectedEmployeeId) return;
    setLoadingWeeklySchedule(true);
    try {
      await axios.delete(`${API_BASE}/timeSlot/weekly/${selectedEmployeeId}`, {
        headers: getAuthHeaders(),
      });
      setWeeklySchedule({});
      setWeeklyConfigured(false);
      message.success("Se restauró la disponibilidad normal del profesional");
    } catch (err) {
      message.error(err.response?.data?.message || "No se pudo restaurar la disponibilidad");
    } finally {
      setLoadingWeeklySchedule(false);
    }
  };

  const handleToggleSalonFullDay = async () => {
    if (!selectedDateKey) {
      message.warning("Seleccioná una fecha primero");
      return;
    }

    if (!businessHours) {
      message.warning("No se pueden administrar bloqueos para domingos");
      return;
    }

    setLoadingSalonFullDay(true);

    try {
      if (isSalonFullDayBlocked) {
        await axios.delete(
          `${API_BASE}/timeSlot/salon-closures/${selectedDateKey}`,
          {
            headers: getAuthHeaders(),
          }
        );
        message.success("Se desbloqueó la fecha completa del salón");
      } else {
        await axios.post(
          `${API_BASE}/timeSlot/salon-closures`,
          {
            date: selectedDateKey,
          },
          {
            headers: getAuthHeaders(),
          }
        );
        message.success("Se bloqueó la fecha completa del salón");
      }

      fetchBlocks();
      fetchSalonClosures();
      fetchReservations();
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.message ||
          "No se pudo actualizar el bloqueo del salón"
      );
    } finally {
      setLoadingSalonFullDay(false);
    }
  };

  return (
    <div style={{ maxWidth: 1220, margin: "0 auto", padding: "28px 24px 40px" }}>
      <Card
        bordered={false}
        style={{
          ...pageCardStyle,
          marginBottom: 24,
          overflow: "hidden",
          background:
            "linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(255,255,255,0.98) 52%, rgba(240,249,255,0.98) 100%)",
        }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} lg={14}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Text
                style={{
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  color: "#2563eb",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                Gestión de disponibilidad
              </Text>
              <Title level={2} style={{ margin: 0, color: "#0f172a" }}>
                Bloqueos y cierres de agenda
              </Title>
              <Text style={{ color: "#64748b", fontSize: 15, lineHeight: 1.7 }}>
                Administrá horarios individuales, fechas completas por profesional
                o cierres generales del salón desde una sola vista clara y rápida.
              </Text>
              <Space size={[10, 10]} wrap>
                <Tag
                  icon={<ClockCircleOutlined />}
                  color="processing"
                  style={{ borderRadius: 999, paddingInline: 12, paddingBlock: 6 }}
                >
                  Jornada: {businessHoursLabel}
                </Tag>
                <Tag
                  icon={<CalendarOutlined />}
                  color="blue"
                  style={{ borderRadius: 999, paddingInline: 12, paddingBlock: 6 }}
                >
                  Intervalos de {SLOT_STEP_MINUTES} min
                </Tag>
              </Space>
            </Space>
          </Col>

          <Col xs={24} lg={10}>
            <div style={selectionPanelStyle}>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Text strong style={{ color: "#0f172a" }}>
                    Profesional
                  </Text>
                  <Select
                    value={selectedEmployeeId}
                    onChange={setSelectedEmployeeId}
                    placeholder="Seleccioná un profesional"
                    style={{ width: "100%", marginTop: 8 }}
                    size="large"
                    options={employees.map((employee) => ({
                      value: employee.id,
                      label: employee.name,
                    }))}
                  />
                </Col>

                <Col xs={24}>
                  <Text strong style={{ color: "#0f172a" }}>
                    Fecha de trabajo
                  </Text>
                  <DatePicker
                    value={selectedDate}
                    onChange={(value) => setSelectedDate(value)}
                    style={{ width: "100%", marginTop: 8 }}
                    format="DD/MM/YYYY"
                    size="large"
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Card>

      {selectedEmployeeId && (
        <Card
          bordered={false}
          loading={loadingWeeklySchedule}
          style={{
            ...pageCardStyle,
            marginBottom: 24,
            background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
          }}
        >
          <Row gutter={[20, 20]} align="middle" style={{ marginBottom: 20 }}>
            <Col xs={24} lg={9}>
              <Space align="start" size={14}>
                <div
                  style={{
                    width: 46,
                    height: 46,
                    flexShrink: 0,
                    borderRadius: 15,
                    background: "#111827",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LockOutlined style={{ color: "#fff", fontSize: 19 }} />
                </div>
                <div>
                  <Text strong style={{ display: "block", color: "#0f172a", fontSize: 16 }}>
                    Bloqueo permanente de {selectedEmployee?.name}
                  </Text>
                  <Text style={{ color: "#64748b", lineHeight: 1.6 }}>
                    Bloquea todos los horarios de todas las fechas, meses y años.
                    No depende de la fecha seleccionada arriba.
                  </Text>
                </div>
              </Space>
            </Col>

            <Col xs={24} lg={15}>
              <Space
                wrap={false}
                style={{ width: "100%", justifyContent: "flex-end" }}
              >
                <Button
                  type="primary"
                  danger
                  size="large"
                  icon={<LockOutlined />}
                  onClick={handleBlockAllWeekly}
                  style={{ borderRadius: 12, fontWeight: 700 }}
                >
                  Bloquear para siempre todas las fechas y horarios
                </Button>
                {weeklyConfigured && (
                  <Button size="large" onClick={handleRemoveWeeklySchedule} style={{ borderRadius: 12 }}>
                    Restaurar horario normal
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12} xl={6}>
          <Card bordered={false} style={summaryCardStyle}>
            <Statistic
              title="Profesional"
              value={selectedEmployee?.name || "Sin seleccionar"}
              prefix={<TeamOutlined style={{ color: "#2563eb" }} />}
              valueStyle={{ color: "#0f172a", fontSize: 26 }}
            />
          </Card>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Card bordered={false} style={summaryCardStyle}>
            <Statistic
              title="Fecha elegida"
              value={selectedDateLabel}
              prefix={<CalendarOutlined style={{ color: "#2563eb" }} />}
              valueStyle={{ color: "#0f172a", fontSize: 20 }}
            />
          </Card>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Card bordered={false} style={summaryCardStyle}>
            <Space direction="vertical" size={8}>
              <Text style={{ color: "#64748b", fontSize: 14 }}>
                Estado del salón
              </Text>
              <Tag
                color={salonStatus.color}
                style={{
                  width: "fit-content",
                  borderRadius: 999,
                  paddingInline: 12,
                  paddingBlock: 6,
                  fontWeight: 600,
                }}
              >
                {salonStatus.text}
              </Tag>
              <Text style={{ color: "#94a3b8", fontSize: 13 }}>
                {isSalonFullDayBlocked
                  ? "La fecha queda cerrada para todos los profesionales."
                  : "La fecha sigue habilitada para reservas."}
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Card bordered={false} style={summaryCardStyle}>
            <Space direction="vertical" size={8}>
              <Text style={{ color: "#64748b", fontSize: 14 }}>
                Estado del profesional
              </Text>
              <Tag
                color={employeeStatus.color}
                style={{
                  width: "fit-content",
                  borderRadius: 999,
                  paddingInline: 12,
                  paddingBlock: 6,
                  fontWeight: 600,
                }}
              >
                {employeeStatus.text}
              </Tag>
              <Text style={{ color: "#94a3b8", fontSize: 13 }}>
                {selectedEmployee
                  ? "Podés bloquear solo los huecos libres de la agenda."
                  : "Elegí primero a quién querés administrar."}
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card bordered={false} style={{ ...pageCardStyle, marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <div
              style={{
                height: "100%",
                borderRadius: 20,
                border: "1px solid #e2e8f0",
                padding: 20,
                background: "#fcfdff",
              }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space align="center">
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 14,
                      background: "#eff6ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShopOutlined style={{ color: "#2563eb", fontSize: 18 }} />
                  </div>
                  <div>
                    <Text strong style={{ display: "block", color: "#0f172a" }}>
                      Cierre general del salón
                    </Text>
                    <Text style={{ color: "#64748b" }}>
                      Bloquea la fecha completa para todos los profesionales.
                    </Text>
                  </div>
                </Space>

                <Button
                  type={isSalonFullDayBlocked ? "default" : "primary"}
                  onClick={handleToggleSalonFullDay}
                  loading={loadingSalonFullDay}
                  disabled={!businessHours}
                  size="large"
                  style={{
                    height: 48,
                    borderRadius: 14,
                    fontWeight: 700,
                  }}
                >
                  {isSalonFullDayBlocked
                    ? "Desbloquear fecha del salón"
                    : "Bloquear fecha del salón"}
                </Button>
              </Space>
            </div>
          </Col>

          <Col xs={24} xl={12}>
            <div
              style={{
                height: "100%",
                borderRadius: 20,
                border: "1px solid #e2e8f0",
                padding: 20,
                background: "#fcfdff",
              }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space align="center">
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 14,
                      background: "#ecfeff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LockOutlined style={{ color: "#0891b2", fontSize: 18 }} />
                  </div>
                  <div>
                    <Text strong style={{ display: "block", color: "#0f172a" }}>
                      Bloqueo completo del profesional
                    </Text>
                    <Text style={{ color: "#64748b" }}>
                      Cierra todos los huecos libres del día seleccionado.
                    </Text>
                  </div>
                </Space>

                <Button
                  type={isFullDayBlocked ? "default" : "primary"}
                  onClick={handleToggleFullDay}
                  loading={loadingFullDay}
                  disabled={
                    !selectedEmployeeId ||
                    !businessHours ||
                    !manageableSlots.length ||
                    isSalonFullDayBlocked ||
                    weeklyConfigured
                  }
                  size="large"
                  style={{
                    height: 48,
                    borderRadius: 14,
                    fontWeight: 700,
                  }}
                >
                  {weeklyConfigured
                    ? "Bloqueo permanente activo"
                    : isFullDayBlocked
                      ? "Desbloquear fecha del profesional"
                      : "Bloquear fecha del profesional"}
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Card bordered={false} style={pageCardStyle} loading={fetching}>
        <Space
          direction="vertical"
          size={18}
          style={{ width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div>
              <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                Horarios del día
              </Title>
              <Text style={{ color: "#64748b" }}>
                Tocá un horario libre para bloquearlo o uno bloqueado para liberarlo.
              </Text>
            </div>

            <Space wrap size={[10, 10]}>
              <Tag
                style={{
                  borderRadius: 999,
                  paddingInline: 12,
                  paddingBlock: 6,
                  borderColor: "#dbeafe",
                  color: "#2563eb",
                  background: "#eff6ff",
                  fontWeight: 600,
                }}
              >
                Libres: {availableCount}
              </Tag>
              <Tag
                style={{
                  borderRadius: 999,
                  paddingInline: 12,
                  paddingBlock: 6,
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  background: "#f3f4f6",
                  fontWeight: 600,
                }}
              >
                Reservados: {reservedCount}
              </Tag>
              <Tag
                style={{
                  borderRadius: 999,
                  paddingInline: 12,
                  paddingBlock: 6,
                  borderColor: "#111827",
                  color: "#ffffff",
                  background: "#111827",
                  fontWeight: 600,
                }}
              >
                Bloqueados: {blockedCount}
              </Tag>
            </Space>
          </div>

          <Alert
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Los turnos reservados se protegen automáticamente"
            description="Solo podés administrar huecos libres. Los horarios con reserva aparecen deshabilitados para evitar conflictos."
            style={{ borderRadius: 18 }}
          />

          <Space wrap size={[12, 12]}>
            <div style={legendItemStyle}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "#2563eb",
                }}
              />
              <Text style={{ color: "#0f172a" }}>Disponible</Text>
            </div>

            <div style={legendItemStyle}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "#111827",
                }}
              />
              <Text style={{ color: "#0f172a" }}>Bloqueado manualmente</Text>
            </div>

            <div style={legendItemStyle}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "#d1d5db",
                }}
              />
              <Text style={{ color: "#0f172a" }}>Reservado</Text>
            </div>
          </Space>

          {!businessHours ? (
            <Empty
              description="Los domingos no tienen horarios disponibles para administrar."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : !selectedEmployeeId ? (
            <Empty
              description="Seleccioná un profesional para ver y administrar su agenda."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : isSalonFullDayBlocked ? (
            <Alert
              type="warning"
              showIcon
              message="El salón completo está bloqueado en esta fecha"
              description="Desbloqueá primero la fecha general del salón para volver a administrar horarios individuales."
              style={{ borderRadius: 18 }}
            />
          ) : (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              {weeklyConfigured ? (
                <Alert
                  type={isPermanentFullDayBlocked ? "error" : "warning"}
                  showIcon
                  message={
                    isPermanentFullDayBlocked
                      ? "Este profesional tiene toda la agenda bloqueada permanentemente"
                      : "Bloqueo permanente activo con horarios habilitados manualmente"
                  }
                  description="Tocá un horario negro para habilitarlo solamente en esta fecha. Tocá uno disponible para volver a bloquearlo."
                  style={{ borderRadius: 18 }}
                />
              ) : null}
              {isFullDayBlocked ? (
                <Alert
                  type="warning"
                  showIcon
                  message="La agenda libre del profesional está bloqueada por completo"
                  description="Podés volver a habilitarla desde el botón de bloqueo completo o tocando un horario individual."
                  style={{ borderRadius: 18 }}
                />
              ) : null}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                  gap: 14,
                }}
              >
                {slots.map((time) => {
                  const isBlocked =
                    blockedByTime.has(time) || isPermanentlyBlockedTime(time);
                  const isReserved = reservedTimes.has(time);

                  return (
                    <Button
                      key={time}
                      onClick={() => handleToggleBlock(time)}
                      loading={loadingTime === time}
                      disabled={!selectedEmployeeId || isReserved || isSalonFullDayBlocked}
                      style={getSlotButtonStyle({ isBlocked, isReserved })}
                    >
                      {time}
                    </Button>
                  );
                })}
              </div>
            </Space>
          )}
        </Space>
      </Card>
    </div>
  );
}
